/**
 * ForecastReview — Review-Arbeitsbereich für Kunden-Forecasts (#1632).
 *
 * Segmente (URL ?seg=):
 * - reviews (default): monats-unabhängige Review-Queue (submitted/in_review nach Alter,
 *   SLA-Badges grün <3 T. / gelb 3–6 / rot ab forecast_review_escalation_days),
 *   Change-Requests, Aktionszeiträume-Genehmigung, Monats-Status mit Erinnern.
 * - aktionszeitraeume: komplette AZ-Verwaltung (AktionszeitraeumePage embedded).
 *
 * Ersetzt /kunden-forecasts (Redirect) und /forecast/aktionszeitraeume (Redirect mit seg).
 * IST-Werte-Sync wurde in den Qualität-Tab verlegt (Admin-Kontext).
 *
 * @page /forecast-review
 * @roles admin, manager
 * @api /api/customer-forecasts/pending, /change-requests, /status-overview, /api/aktionszeitraeume
 * @i18n forecastReview, customerForecast, aktionszeitraeume
 * @since v2.789.0
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileEdit, Check, X, Zap, Bell, Inbox, Info } from 'lucide-react';
import { Card, PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import SharedForecastTabs from '../components/SharedForecastTabs';
import AktionszeitraeumePage from './AktionszeitraeumePage';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts })
    .then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      return data;
    });
}

function fmt(n) {
  if (n == null) return '\u2013';
  return Number(n).toLocaleString('de-DE');
}

function ageDays(ts) {
  if (!ts) return null;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
}

export function StatusBadge({ status, t }) {
  const cfg = {
    draft: { bg: 'bg-gray-500/10', text: 'text-[var(--text-secondary)]', label: t('forecastReview.statusDraft') },
    submitted: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: t('forecastReview.statusSubmitted') },
    in_review: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: t('forecastReview.statusInReview') },
    approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', label: t('forecastReview.statusApproved') },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-600', label: t('forecastReview.statusRejected') },
    missing: { bg: 'bg-gray-500/10', text: 'text-[var(--text-tertiary)]', label: t('forecastReview.statusMissing') },
    overdue: { bg: 'bg-red-500/10', text: 'text-red-600', label: t('forecastReview.statusOverdue') },
    fallback: { bg: 'bg-cyan-500/10', text: 'text-cyan-600', label: t('forecastReview.statusFallback') },
  };
  const c = cfg[status] || cfg.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

export function AbgeberPill({ t }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[var(--accent-muted,#EBF4FC)] text-[var(--accent,#338edc)]">
      {t('forecastReview.abgeber')}
    </span>
  );
}

function SlaBadge({ days, escalationDays, t }) {
  if (days == null) return null;
  const cls = days >= escalationDays
    ? 'bg-red-500/10 text-red-600'
    : days >= 3
      ? 'bg-amber-500/10 text-amber-600'
      : 'bg-emerald-500/10 text-emerald-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      {days} {t('forecastReview.slaDays')}
    </span>
  );
}

export function StatusLegend({ t }) {
  const items = ['statusDraft', 'statusSubmitted', 'statusInReview', 'statusApproved', 'statusRejected', 'statusMissing', 'statusOverdue'];
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px] text-[var(--text-tertiary)]">
      <Info className="w-3.5 h-3.5" />
      <span className="font-semibold">{t('forecastReview.legend')}:</span>
      {items.map(k => <span key={k}>{t(`forecastReview.${k}`)}</span>).reduce((acc, el, i) => acc === null ? [el] : [...acc, <span key={`sep${i}`}>·</span>, el], null)}
      <span className="ml-2">({t('forecastReview.legendComputed')})</span>
    </div>
  );
}

export default function ForecastReview() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const seg = searchParams.get('seg') === 'aktionszeitraeume' ? 'aktionszeitraeume' : 'reviews';

  const now = new Date();
  const defYear = now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear();
  const defMonth = now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2;
  const year = parseInt(searchParams.get('year')) || defYear;
  const month = parseInt(searchParams.get('month')) || defMonth;

  const setParam = (key, value, def) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (value === def || value == null) p.delete(key); else p.set(key, String(value));
      return p;
    }, { replace: true });
  };

  const [pending, setPending] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [crReviewNotes, setCrReviewNotes] = useState({});
  const [crProcessing, setCrProcessing] = useState(null);
  const [pendingAktionen, setPendingAktionen] = useState([]);
  const [azProcessing, setAzProcessing] = useState(null);
  const [azRejectId, setAzRejectId] = useState(null);
  const [azRejectGrund, setAzRejectGrund] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(null);
  const [toast, setToast] = useState(null);

  const monthStr = `${year}-${String(month).padStart(2, '0')}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingData, crData, azData, stData] = await Promise.all([
        api('/api/customer-forecasts/pending'),
        api('/api/customer-forecasts/change-requests?status=pending'),
        api('/api/aktionszeitraeume?status=eingereicht'),
        api(`/api/customer-forecasts/status-overview?month=${monthStr}`),
      ]);
      setPending(pendingData || []);
      setChangeRequests(crData || []);
      setPendingAktionen(azData || []);
      setStatusData(stData || null);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => { if (seg === 'reviews') loadData(); }, [loadData, seg]);

  const handleCrAction = async (id, action) => {
    setCrProcessing(id);
    try {
      await api(`/api/customer-forecasts/change-requests/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: action === 'approve' ? 'approved' : 'rejected',
          review_note: crReviewNotes[id] || '',
        }),
      });
      setCrReviewNotes(prev => { const n = { ...prev }; delete n[id]; return n; });
      await loadData();
    } catch (err) { console.error('CR action error:', err); }
    finally { setCrProcessing(null); }
  };

  const handleAzApprove = async (id) => {
    setAzProcessing(id);
    try {
      await api(`/api/aktionszeitraeume/${id}/approve`, { method: 'PUT' });
      await loadData();
    } catch (err) { console.error('AZ approve error:', err); }
    finally { setAzProcessing(null); }
  };

  const handleAzReject = async (id) => {
    setAzProcessing(id);
    try {
      await api(`/api/aktionszeitraeume/${id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grund: azRejectGrund }),
      });
      setAzRejectId(null);
      setAzRejectGrund('');
      await loadData();
    } catch (err) { console.error('AZ reject error:', err); }
    finally { setAzProcessing(null); }
  };

  const handleRemind = async (customerId) => {
    setReminding(customerId);
    try {
      const res = await api(`/api/customer-forecasts/${customerId}/remind?month=${monthStr}`, { method: 'POST' });
      setToast(res?.email_sent === false ? t('forecastReview.remindNoPortal') : t('forecastReview.remindSent'));
      setTimeout(() => setToast(null), 4000);
      await loadData();
    } catch (err) {
      console.error('Remind error:', err);
      setToast(err.message);
      setTimeout(() => setToast(null), 4000);
    } finally { setReminding(null); }
  };

  const escalationDays = statusData?.escalation_days || 7;
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const monthNames = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  return (
    <div className="p-6">
      <SharedForecastTabs badges={{ fc_pending: pending.length, az_pending: pendingAktionen.length }} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader title={t('forecastReview.title')} subtitle={t('forecastReview.subtitle')} />
        <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1">
          {[
            { key: 'reviews', label: t('forecastReview.segReviews') },
            { key: 'aktionszeitraeume', label: t('forecastReview.segAktionszeitraeume') },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setParam('seg', s.key, 'reviews')}
              className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                seg === s.key
                  ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-lg bg-[var(--bg-card)] border border-[var(--border-card)] shadow-lg text-sm text-[var(--text-primary)]">
          {toast}
        </div>
      )}

      {seg === 'aktionszeitraeume' ? (
        <AktionszeitraeumePage embedded />
      ) : loading && !statusData ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Review-Queue: monats-unabhängig, nach Alter */}
          <Card className="mb-6">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Inbox className="w-4 h-4 text-[var(--accent,#338edc)]" />
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('forecastReview.queueTitle')} ({pending.length})
                </span>
              </div>
              <span className="text-[11px] text-[var(--text-tertiary)]">
                {t('forecastReview.slaLegend', { esc: escalationDays })}
              </span>
            </div>
            {pending.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('forecastReview.queueEmpty')}</div>
            ) : (
              <div className="grid gap-2">
                {pending.map(p => {
                  const days = ageDays(p.submitted_at);
                  return (
                    <div
                      key={p.id}
                      onClick={() => navigate(`/kunden-forecasts/${p.id}`)}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card)] cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-medium text-[var(--text-primary)] truncate">{p.kunden_name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-secondary)] whitespace-nowrap">
                          {monthNames[p.month]} {p.year}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm font-mono text-[var(--text-primary)]">{fmt(p.total_forecast)}</span>
                        <StatusBadge status={p.status} t={t} />
                        <SlaBadge days={days} escalationDays={escalationDays} t={t} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Change Requests */}
          {changeRequests.length > 0 && (
            <div className="mb-6 border border-amber-500/20 bg-amber-500/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileEdit className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('forecast.changeRequests.title')} ({changeRequests.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-amber-500/20">
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('forecast.changeRequests.customer')}</th>
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('forecast.changeRequests.month')}</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('forecast.changeRequests.oldValue')}</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('forecast.changeRequests.newValue')}</th>
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('forecast.changeRequests.reason')}</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeRequests.map(cr => {
                      const monat = new Date(cr.monat);
                      return (
                        <tr key={cr.id} className="border-b border-amber-500/10 last:border-0">
                          <td className="py-2.5 px-3 font-medium text-[var(--text-primary)]">{cr.kunden_name}</td>
                          <td className="py-2.5 px-3 text-[var(--text-secondary)]">
                            {monthNames[monat.getUTCMonth() + 1]} {monat.getUTCFullYear()}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-[var(--text-secondary)]">{fmt(cr.old_value)}</td>
                          <td className="py-2.5 px-3 text-right font-mono text-[var(--text-primary)] font-semibold">{fmt(cr.new_value)}</td>
                          <td className="py-2.5 px-3 text-[var(--text-secondary)] max-w-[200px] truncate" title={cr.reason}>{cr.reason || '–'}</td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2 justify-end">
                              <input
                                type="text"
                                placeholder={t('forecast.changeRequests.reviewNote')}
                                className="px-2 py-1 text-xs rounded border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] w-32"
                                value={crReviewNotes[cr.id] || ''}
                                onChange={e => setCrReviewNotes(prev => ({ ...prev, [cr.id]: e.target.value }))}
                              />
                              <button
                                onClick={() => handleCrAction(cr.id, 'approve')}
                                disabled={crProcessing === cr.id}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                              >
                                <Check className="w-3.5 h-3.5" />
                                {t('forecast.changeRequests.approve')}
                              </button>
                              <button
                                onClick={() => handleCrAction(cr.id, 'reject')}
                                disabled={crProcessing === cr.id}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                              >
                                <X className="w-3.5 h-3.5" />
                                {t('forecast.changeRequests.reject')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Aktionszeiträume zur Genehmigung */}
          {pendingAktionen.length > 0 && (
            <div className="mb-6 border border-purple-500/20 bg-purple-500/5 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    {t('aktionszeitraeume.review.title', { count: pendingAktionen.length })}
                  </span>
                </div>
                <button
                  onClick={() => setParam('seg', 'aktionszeitraeume', 'reviews')}
                  className="text-xs text-[var(--accent,#338edc)] hover:underline"
                >
                  {t('forecastReview.azManageLink')}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('aktionszeitraeume.kunde')}</th>
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('aktionszeitraeume.titel')}</th>
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('aktionszeitraeume.typ')}</th>
                      <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('aktionszeitraeume.zeitraum')}</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('aktionszeitraeume.multiplikator')}</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingAktionen.map(az => {
                      const typColors = { black_week: '#EF4444', sale: '#F59E0B', launch: '#3B82F6', kampagne: '#8B5CF6', saison: '#06B6D4', sonstige: '#6B7280' };
                      const tc = typColors[az.typ] || '#6B7280';
                      return (
                        <tr key={az.id} className="border-b border-purple-500/10 last:border-0">
                          <td className="py-2.5 px-3 font-medium text-[var(--text-primary)]">{az.kunde_name}</td>
                          <td className="py-2.5 px-3 text-[var(--text-primary)]">{az.titel}</td>
                          <td className="py-2.5 px-3">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: tc + '20', color: tc }}>
                              {t(`forecastPromo.typ.${az.typ}`)}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-[var(--text-secondary)] whitespace-nowrap">
                            {new Date(az.datum_von).toLocaleDateString('de-AT')} – {new Date(az.datum_bis).toLocaleDateString('de-AT')}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-[var(--text-primary)]">
                            {az.multiplikator ? `${az.multiplikator}x` : `+${fmt(az.zusatz_orders)}`}
                          </td>
                          <td className="py-2.5 px-3">
                            {azRejectId === az.id ? (
                              <div className="flex items-center gap-2 justify-end">
                                <input
                                  type="text"
                                  placeholder={t('aktionszeitraeume.review.rejectReason')}
                                  className="px-2 py-1 text-xs rounded border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] w-36"
                                  value={azRejectGrund}
                                  onChange={e => setAzRejectGrund(e.target.value)}
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleAzReject(az.id)}
                                  disabled={azProcessing === az.id}
                                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => { setAzRejectId(null); setAzRejectGrund(''); }}
                                  className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                                >
                                  {t('confirm.abbrechen')}
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 justify-end">
                                <button
                                  onClick={() => handleAzApprove(az.id)}
                                  disabled={azProcessing === az.id}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                  {t('aktionszeitraeume.review.approve')}
                                </button>
                                <button
                                  onClick={() => setAzRejectId(az.id)}
                                  disabled={azProcessing === az.id}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                                >
                                  <X className="w-3.5 h-3.5" />
                                  {t('aktionszeitraeume.review.reject')}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Monats-Status + Erinnern */}
          <Card>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <span className="text-sm font-semibold text-[var(--text-primary)]">{t('forecastReview.monthStatusTitle')}</span>
              <div className="flex items-center gap-2">
                <select
                  value={year}
                  onChange={e => setParam('year', parseInt(e.target.value), defYear)}
                  className="px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] text-sm"
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select
                  value={month}
                  onChange={e => setParam('month', parseInt(e.target.value), defMonth)}
                  className="px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] text-sm"
                >
                  {months.map(m => <option key={m} value={m}>{t(`common.months.${m - 1}`)}</option>)}
                </select>
              </div>
            </div>
            {!statusData ? (
              <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('customerForecast.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-card)]">
                      <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('customerForecast.colCustomer')}</th>
                      <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('customerForecast.colSegment')}</th>
                      <th className="text-right py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('customerForecast.colForecast')}</th>
                      <th className="text-center py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('customerForecast.colStatus')}</th>
                      <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('customerForecast.colDeadline')}</th>
                      <th className="text-right py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(statusData.customers || []).filter(c => c.is_abgeber || c.status !== 'missing').map(c => (
                      <tr
                        key={c.customer_id}
                        onClick={() => c.cf_id && navigate(`/kunden-forecasts/${c.cf_id}`)}
                        className={`border-b border-[var(--border-card)] transition-colors ${c.cf_id ? 'cursor-pointer hover:bg-[var(--bg-hover)]' : ''}`}
                      >
                        <td className="py-3 px-4 font-medium text-[var(--text-primary)]">
                          <span className="flex items-center gap-2">
                            {c.name}
                            {c.is_abgeber && <AbgeberPill t={t} />}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-[var(--text-secondary)]">{c.segment || '\u2013'}</td>
                        <td className="py-3 px-4 text-right font-mono text-[var(--text-primary)]">{c.soll != null ? fmt(c.soll) : '\u2013'}</td>
                        <td className="py-3 px-4 text-center"><StatusBadge status={c.status} t={t} /></td>
                        <td className="py-3 px-4 text-[13px] text-[var(--text-secondary)]">
                          {c.deadline ? new Date(c.deadline).toLocaleDateString('de-DE') : '\u2013'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {c.is_abgeber && ['missing', 'overdue'].includes(c.status) && (
                            <button
                              onClick={e => { e.stopPropagation(); handleRemind(c.customer_id); }}
                              disabled={reminding === c.customer_id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-50"
                            >
                              <Bell className="w-3.5 h-3.5" />
                              {t('forecastReview.remind')}
                              {c.reminder_count > 0 && <span className="text-[10px] text-[var(--text-tertiary)]">({c.reminder_count})</span>}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-[var(--border-card)]">
              <StatusLegend t={t} />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
